/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useEffect, useRef, Fragment } from 'react';

import { jsx } from '@emotion/react';

import { AddressSuggestion } from '../../interfaces/IStreets';
import { option as optionCSS, entries as entriesCSS, label, caret } from './AddressOption.style';
import ChevronIcon from '../../assets/chevron.svg';

export const getOptionString = ({
  street_line,
  secondary,
  city,
  state,
  zipcode,
  entries
}: AddressSuggestion): string => {
  const secondaryStr = secondary ? ` ${secondary}` : '';
  const entriesStr = +entries > 1 ? ` (${entries})` : '';

  return `${street_line}${secondaryStr}${entriesStr} ${city}, ${state} ${zipcode}`;
};

const renderOptionLabel = (inputValue = '', optionString: string, entries: number): any => {
  const inputLow = inputValue.toLowerCase().trim();
  const optionLow = optionString.toLowerCase().trim();
  let i = 0;

  for (; i < optionString.length; i++) if (inputLow[i] === undefined || inputLow[i] !== optionLow[i]) break;

  const common = optionString.slice(0, i);
  const suggested: (string | any)[] = optionString.slice(i).split(`(${entries})`);

  if (entries > 1) {
    suggested.splice(
      1,
      0,
      <span css={entriesCSS} key="entries">
        ({entries} entries)
      </span>
    );
  }

  return (
    <Fragment>
      <strong css={label} data-testid="address-bold">
        {common}
      </strong>
      {suggested.map(sub => sub)}
      {entries > 1 && (
        <svg viewBox="0 0 24 24" xmlnsXlink="http://www.w3.org/1999/xlink" css={caret}>
          <use xlinkHref={`${ChevronIcon}#right`} x="0" y="24" />
        </svg>
      )}
    </Fragment>
  );
};

interface Props {
  inputValue?: string;
  option: AddressSuggestion;
  index: number;
  isFocused?: boolean;
  onSelect(option: AddressSuggestion, optionString: string): void;
  onHover(i: number): void;
  isSelected?: boolean;
}

const AddressOption: React.FC<Props> = ({
  inputValue = '',
  option,
  index,
  onHover,
  onSelect,
  isFocused = false,
  isSelected = false
}) => {
  const optionString = getOptionString(option);
  const optionEl = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (isFocused && optionEl.current) {
      optionEl.current.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
    }
  }, [isFocused]);

  return (
    <button
      type="button"
      css={optionCSS(isFocused)}
      ref={optionEl}
      onClick={() => onSelect(option, optionString)}
      onMouseEnter={() => onHover(index)}
      title={optionString}
      role="option"
      data-testid="option"
      aria-selected={isSelected}
    >
      {renderOptionLabel(inputValue, optionString, +option.entries)}
    </button>
  );
};

export default AddressOption;
